import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  INTEGRATION_PROVIDERS,
  callsThisMonth,
  canManageIntegrations,
  canReplaceCredential,
  checkCredential,
  credentialWord,
  DOCUSEAL_WEBHOOK_HEADER,
  docusealWebhookUrl,
  integrationStatusText,
  type IntegrationProviderDef,
  type IntegrationRow,
  type ProviderCategory,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { loadIntegrations, removeIntegration, saveIntegration, type CredentialInput } from './api.ts';
import { ArchivePanel } from './ArchivePanel.tsx';
import { readConfig } from '../config.ts';

export interface IntegrationsScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly roles: readonly string[];
  /** For the "calls this month" figure; tests pass a fixed time. */
  readonly now?: () => Date;
}

const GROUPS: readonly (readonly [ProviderCategory, string])[] = [
  ['tracker', 'Aircraft trackers'],
  ['aviation', 'Aviation'],
  ['weather', 'Weather'],
  ['sensor', 'Basemag stations'],
  // decision 687: the organisation's own BoM Space Weather key; the public sources need nothing
  ['space_weather', 'Space weather'],
  // R120, decisions 133 and 158: the organisation's own DocuSeal account signs the JSP safety meeting
  ['signing', 'Document signing'],
  // F09, decision 507: the organisation's own Claude key reads MR pages, fuel dockets and receipts from a photo
  ['reading', 'Document reading'],
];

/** The form being filled: a new integration (row null) or a replacement value for `row`. */
interface Editing {
  readonly provider: IntegrationProviderDef;
  readonly row: IntegrationRow | null;
}

/**
 * What the last save or remove came to, shown in the group it happened in (MB, 3 Nov 2026: a notice at the top of
 * the page was out of view when the Claude key, in the last group, was refused, so Save seemed to do nothing).
 */
interface Notice {
  readonly ok: boolean;
  readonly text: string;
  readonly category: ProviderCategory;
}

/** The notice of a group: "… is set up." or the refusal in words, scrolled into view when it appears or changes. */
function GroupNotice({ notice }: { readonly notice: Notice }): ReactElement {
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [notice]);
  return (
    <p
      ref={ref}
      className={notice.ok ? 'notice' : 'notice notice--fail'}
      role={notice.ok ? 'status' : 'alert'}
    >
      {notice.text}
    </p>
  );
}

const when = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Never';

/**
 * Integrations (ADR-016 §1; docs/specs/integrations.md §5): each outside service the organisation connects with its
 * own access key or login. Tenant admins add, replace and remove; Field users (manager, field crew, data processor)
 * replace a tracker login when its password changes (MB, Q71). A stored value is never shown again: the screen
 * shows its last four characters, its status, when it was last used and how many calls were made this month.
 */
export function IntegrationsScreen({
  sb,
  tenantId,
  roles,
  now = () => new Date(),
}: IntegrationsScreenProps): ReactElement {
  const [rows, setRows] = useState<IntegrationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  /** A save or remove is on its way to Teslyn Cloud: its button says so and nothing else can be pressed. */
  const [busy, setBusy] = useState(false);
  const admin = canManageIntegrations(roles);

  const reload = useCallback(() => {
    setError(null);
    loadIntegrations(sb, tenantId)
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  /**
   * Run a save or remove and say how it went in `category`'s group. A failure keeps the form open with its words
   * above it, so the person can read why and try again; the typed value is never part of the words.
   */
  async function run(action: () => Promise<void>, done: string, category: ProviderCategory): Promise<void> {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await action();
      setNotice({ ok: true, text: done, category });
      setEditing(null);
      setConfirmRemove(null);
    } catch (e: unknown) {
      const text = e instanceof Error && e.message ? e.message : 'Teslyn Cloud did not answer. Try again.';
      setNotice({ ok: false, text, category });
    }
    setBusy(false);
    reload();
  }

  return (
    <section className="page">
      <PageHeader
        title="Integrations"
        subtitle="Your organisation’s own access keys and logins for trackers, aviation and weather services, and where its files are archived."
      />
      <p className="body">
        Teslyn calls these services from its server with the value stored here, so no key or password is kept
        on a laptop or in a browser. A stored value is encrypted and can never be shown again, not even to a
        tenant admin: to change it, replace it.
      </p>
      {error && <ErrorState message={error} onRetry={reload} />}
      {!rows && !error && <Loading what="integrations" />}
      {rows &&
        GROUPS.map(([category, title]) => (
          <article key={category} className="panel stack" aria-label={title}>
            <h2 className="heading">{title}</h2>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="label">Service</th>
                    <th className="label">Status</th>
                    <th className="label">Stored value</th>
                    <th className="label">Last used</th>
                    <th className="label">Calls this month</th>
                    <th className="label">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {INTEGRATION_PROVIDERS.filter((p) => p.category === category).flatMap((p) => {
                    const mine = rows.filter((r) => r.provider === p.id);
                    const name = (r: IntegrationRow): string => (r.label ? `${p.name}, ${r.label}` : p.name);
                    if (p.credential === 'none') {
                      return [
                        <tr key={`${p.id}:public`}>
                          <td>{p.name}</td>
                          <td>
                            <span className="chip chip--pass caption">Public, nothing to set up</span>
                          </td>
                          <td className="muted" colSpan={4}>
                            {p.whereToFind}
                          </td>
                        </tr>,
                      ];
                    }
                    const out = mine.map((r) => (
                      <tr key={r.id}>
                        <td>{name(r)}</td>
                        <td>
                          <span
                            className={
                              r.status === 'failing' ? 'chip chip--fail caption' : 'chip chip--pass caption'
                            }
                          >
                            {integrationStatusText(r)}
                          </span>
                        </td>
                        <td className="data">…{r.last_four}</td>
                        <td>{when(r.last_used_at)}</td>
                        <td className="data">{callsThisMonth(r, now()).toLocaleString('en-AU')}</td>
                        <td>
                          <div className="row">
                            {canReplaceCredential(roles, p) && (
                              <Button
                                variant="quiet"
                                onClick={() => setEditing({ provider: p, row: r })}
                                aria-label={`Replace ${name(r)}`}
                              >
                                Replace
                              </Button>
                            )}
                            {admin &&
                              (confirmRemove === r.id ? (
                                <Button
                                  variant="secondary"
                                  busy={busy}
                                  onClick={() =>
                                    void run(
                                      () => removeIntegration(sb, tenantId, r.id),
                                      `${name(r)} was removed.`,
                                      category,
                                    )
                                  }
                                >
                                  {busy ? 'Removing…' : 'Confirm remove'}
                                </Button>
                              ) : (
                                <Button
                                  variant="quiet"
                                  onClick={() => setConfirmRemove(r.id)}
                                  aria-label={`Remove ${name(r)}`}
                                >
                                  Remove
                                </Button>
                              ))}
                          </div>
                        </td>
                      </tr>
                    ));
                    if (mine.length === 0 || (admin && p.multipleAccounts)) {
                      out.push(
                        <tr key={`${p.id}:add`}>
                          <td>
                            {mine.length === 0 ? (
                              p.name
                            ) : (
                              <span className="muted">Another {p.name} account</span>
                            )}
                          </td>
                          <td>
                            {mine.length === 0 ? (
                              <span className="chip chip--role caption">Not set up</span>
                            ) : (
                              ''
                            )}
                          </td>
                          <td className="muted" colSpan={3}>
                            {mine.length === 0 && !admin ? 'Ask a tenant admin to add it.' : ''}
                          </td>
                          <td>
                            {admin && (
                              <Button
                                onClick={() => setEditing({ provider: p, row: null })}
                                aria-label={`Add ${p.name}`}
                              >
                                Add
                              </Button>
                            )}
                          </td>
                        </tr>,
                      );
                    }
                    return out;
                  })}
                </tbody>
              </table>
            </div>
            {category === 'signing' && <DocusealNotice />}
            {/* Issue #17 and MB's 3 Nov 2026 report: the form and the answer to it open in the group of the service
                pressed, not at the top of the page where a lower group cannot see them. */}
            {notice && notice.category === category && <GroupNotice notice={notice} />}
            {editing && editing.provider.category === category && (
              <CredentialForm
                key={`${editing.provider.id}:${editing.row?.id ?? 'new'}`}
                editing={editing}
                busy={busy}
                onCancel={() => setEditing(null)}
                onSave={(label, value) =>
                  void run(
                    () =>
                      saveIntegration(sb, {
                        tenantId,
                        provider: editing.provider.id,
                        label,
                        integrationId: editing.row?.id ?? null,
                        value,
                      }),
                    editing.row
                      ? `The ${editing.provider.name} ${editing.provider.credential === 'api_key' ? 'key' : credentialWord(editing.provider)} was replaced.`
                      : `${editing.provider.name} is set up.`,
                    category,
                  )
                }
              />
            )}
          </article>
        ))}
      {rows && (
        // decision 376: Teslyn Cloud storage, or the organisation's own Nextcloud
        <article className="panel stack" aria-label="File archive">
          <h2 className="heading">File archive</h2>
          <ArchivePanel
            sb={sb}
            tenantId={tenantId}
            roles={roles}
            integration={rows.find((r) => r.provider === 'nextcloud') ?? null}
            onChanged={reload}
          />
        </article>
      )}
    </section>
  );
}

/**
 * What a tenant admin sets up once in DocuSeal so Teslyn hears when the crew sign (docs/specs/cloud/docuseal.md §4).
 * The secret's value is Braynewave's (a Supabase function secret), never shown here.
 */
function DocusealNotice({
  supabaseUrl = readConfig().supabaseUrl,
}: {
  readonly supabaseUrl?: string;
}): ReactElement {
  return (
    <div className="stack" aria-label="DocuSeal notifications">
      <p className="body">
        So Teslyn knows when the crew sign, add a webhook once in DocuSeal (Settings, Webhooks): the address
        below, the events form.viewed, form.completed, form.declined, submission.completed, submission.expired
        and submission.archived, and under Security, Secret, the key{' '}
        <span className="data">{DOCUSEAL_WEBHOOK_HEADER}</span> with the value Braynewave gives you. Without
        it, &quot;Check DocuSeal now&quot; on the Safety sign-off page still brings the signatures in.
      </p>
      <p className="data">
        {docusealWebhookUrl(supabaseUrl || 'https://<your Teslyn Cloud project>.supabase.co')}
      </p>
    </div>
  );
}

function CredentialForm({
  editing,
  busy,
  onCancel,
  onSave,
}: {
  readonly editing: Editing;
  /** The value is on its way to Teslyn Cloud: Save says so and the form waits for the answer. */
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSave: (label: string, value: CredentialInput) => void;
}): ReactElement {
  const { provider: p, row } = editing;
  const [label, setLabel] = useState(row?.label ?? '');
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const value = (): CredentialInput =>
    p.credential === 'api_key'
      ? { api_key: a }
      : p.credential === 'login'
        ? { username: a, password: b }
        : { url: a };
  return (
    <form
      className="panel stack"
      aria-label={row ? `Replace ${p.name}` : `Add ${p.name}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        const checked = checkCredential(p, value());
        if (!checked.ok) {
          setProblem(checked.error);
          return;
        }
        onSave(label.trim(), value());
      }}
    >
      <h2 className="heading">
        {row ? `Replace the ${p.credential === 'api_key' ? 'key' : credentialWord(p)}: ` : 'Add '}
        {row?.label ? `${p.name}, ${row.label}` : p.name}
      </h2>
      <p className="body muted">{p.whereToFind}</p>
      {p.multipleAccounts && !row && (
        <TextField
          label="Account name"
          autoFocus
          hint="Only needed when you add more than one account, for example “Helicopters”."
          value={label}
          onChange={setLabel}
        />
      )}
      {p.credential === 'api_key' && (
        <TextField
          label="Access key"
          type="password"
          autoComplete="off"
          autoFocus
          value={a}
          onChange={setA}
          error={problem ?? undefined}
        />
      )}
      {p.credential === 'login' && (
        <>
          <TextField
            label={p.loginLabels?.username ?? 'Username'}
            spellCheck={false}
            autoComplete="off"
            autoFocus={!p.multipleAccounts || !!row}
            value={a}
            onChange={setA}
          />
          <TextField
            label={p.loginLabels?.password ?? 'Password'}
            type="password"
            autoComplete="new-password"
            value={b}
            onChange={setB}
            error={problem ?? undefined}
          />
        </>
      )}
      {p.credential === 'feed_url' && (
        <TextField
          label="Feed address"
          type="password"
          autoComplete="off"
          autoFocus
          hint="It holds your login, so it is hidden as you type."
          value={a}
          onChange={setA}
          error={problem ?? undefined}
        />
      )}
      <div className="row">
        <Button variant="primary" type="submit" busy={busy}>
          {row ? (busy ? 'Replacing…' : 'Replace') : busy ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="quiet" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
